/**
 * Chain configuration — Base Sepolia testnet for development,
 * Base mainnet for production. Reads NEXT_PUBLIC_CHAIN_ID to pick.
 *
 * Exposes a `switchToBotworkChain(provider)` helper that prompts the
 * connected wallet to move to the expected chain, adding it to the
 * wallet's chain list first if unknown.
 */

import type { BrowserProvider } from "ethers";

export interface ChainConfig {
  chainId: number;
  chainIdHex: string;
  name: string;
  rpcUrl: string;
  blockExplorer: string;
  nativeCurrency: { name: string; symbol: string; decimals: number };
}

const BASE_SEPOLIA: ChainConfig = {
  chainId: 84532,
  chainIdHex: "0x14a34",
  name: "Base Sepolia",
  rpcUrl: "https://sepolia.base.org",
  blockExplorer: "https://sepolia.basescan.org",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
};

const BASE_MAINNET: ChainConfig = {
  chainId: 8453,
  chainIdHex: "0x2105",
  name: "Base",
  rpcUrl: "https://mainnet.base.org",
  blockExplorer: "https://basescan.org",
  nativeCurrency: { name: "Ether", symbol: "ETH", decimals: 18 },
};

export function getChainConfig(): ChainConfig {
  const envChainId = process.env.NEXT_PUBLIC_CHAIN_ID;
  const envRpc = process.env.NEXT_PUBLIC_RPC_URL;
  const id = envChainId ? Number(envChainId) : 84532;
  const base = id === 8453 ? BASE_MAINNET : BASE_SEPOLIA;
  return envRpc ? { ...base, rpcUrl: envRpc } : base;
}

/** Returns the Basescan URL for a given tx hash. */
export function txExplorerUrl(txHash: string): string {
  return `${getChainConfig().blockExplorer}/tx/${txHash}`;
}

/**
 * Ensure the connected wallet is on the configured Botwork chain.
 * Throws if the user rejects or the wallet has no switch support.
 */
export async function switchToBotworkChain(provider: BrowserProvider): Promise<void> {
  const cfg = getChainConfig();
  const network = await provider.getNetwork();
  if (Number(network.chainId) === cfg.chainId) return;

  const eth = (window as unknown as { ethereum?: { request(args: { method: string; params?: unknown[] }): Promise<unknown> } }).ethereum;
  if (!eth) throw new Error("No wallet provider detected.");

  try {
    await eth.request({ method: "wallet_switchEthereumChain", params: [{ chainId: cfg.chainIdHex }] });
  } catch (err: unknown) {
    // 4902 = chain not added in the wallet. Try adding it.
    const code = (err as { code?: number })?.code;
    if (code === 4902) {
      await eth.request({
        method: "wallet_addEthereumChain",
        params: [
          {
            chainId: cfg.chainIdHex,
            chainName: cfg.name,
            rpcUrls: [cfg.rpcUrl],
            blockExplorerUrls: [cfg.blockExplorer],
            nativeCurrency: cfg.nativeCurrency,
          },
        ],
      });
    } else {
      throw err;
    }
  }
}
